import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { siteConfig } from '@/mocks/homeData';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: `关于${siteConfig.name}`,
  description: siteConfig.description,
  url: `${siteConfig.url}/about`,
  about: {
    '@type': 'Organization',
    name: siteConfig.name,
    description: siteConfig.description,
    foundingDate: '2020',
    url: siteConfig.url,
  },
};

const values = [
  {
    icon: 'ri-star-line',
    title: '品质至上',
    desc: '精选优质内容，确保每段视频都具有观赏价值和教育意义，为用户提供极致的观看体验。',
  },
  {
    icon: 'ri-lightbulb-line',
    title: '知识传播',
    desc: '深耕水果植物领域，将专业知识以通俗易懂的视频形式呈现，让学习变得轻松有趣。',
  },
  {
    icon: 'ri-global-line',
    title: '开放共享',
    desc: '坚持免费开放的理念，让更多人能够便捷地获取水果植物相关的优质视频资源。',
  },
  {
    icon: 'ri-heart-pulse-line',
    title: '健康生活',
    desc: '倡导健康饮食和绿色生活方式，通过视频内容引导观众关注自然与健康。',
  },
];

const milestones = [
  { year: '2020', event: '向日葵视频平台正式上线，首批上线1000+精品水果视频' },
  { year: '2021', event: '平台用户突破50万，新增西瓜视频、蜜桃视频等分类频道' },
  { year: '2022', event: '完成视频高清化升级，全面支持1080P高清播放体验' },
  { year: '2023', event: '累计用户突破100万，入选年度最佳水果植物类视频平台' },
  { year: '2024', event: '推出移动端优化版本，实现全平台流畅观看体验' },
  { year: '2025', event: '用户突破200万，视频总量超10000部，成为行业领先平台' },
  { year: '2026', event: '持续创新，引入AI推荐算法，为用户提供个性化的视频内容推荐' },
];

const contactInfo = [
  { icon: 'ri-mail-line', label: '电子邮箱', value: 'contact@xiangrikui.com' },
  { icon: 'ri-phone-line', label: '客服电话', value: '400-888-6666' },
  { icon: 'ri-map-pin-line', label: '公司地址', value: '北京市朝阳区科技园区创新路88号' },
  { icon: 'ri-time-line', label: '工作时间', value: '周一至周五 9:00 - 18:00' },
];

export default function AboutPage() {

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as const });
  }, []);

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          {/* Hero */}
          <section className="relative py-16 md:py-24 overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20sunflower%20field%20at%20golden%20hour%2C%20peaceful%20natural%20landscape%2C%20vibrant%20yellow%20flowers%2C%20dreamy%20soft%20light%2C%20artistic%20photography%2C%20gentle%20bokeh%2C%20clean%20composition%20without%20text&width=1800&height=600&seq=about-hero-01&orientation=landscape"
              alt={`关于${siteConfig.name}`}
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/55"></div>
            <div className="relative z-10 max-w-[900px] mx-auto px-4 md:px-6 text-center">
              <h1 className="text-3xl md:text-5xl font-bold text-white font-heading">{`关于${siteConfig.name}`}</h1>
              <p className="text-sm md:text-base text-white/80 mt-4 max-w-xl mx-auto leading-relaxed">
                {siteConfig.description}
              </p>
            </div>
          </section>

          <div className="max-w-[1100px] mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: `关于我们` },
              ]}
            />
          </div>

          {/* Brand Introduction */}
          <section className="py-14 md:py-20" aria-label="品牌介绍">
            <div className="max-w-[900px] mx-auto px-4 md:px-6">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 text-center font-heading mb-4">
                品牌介绍
              </h2>
              <div className="w-16 h-1 bg-primary-500 rounded-full mx-auto mb-8"></div>
              <div className="space-y-5 text-base text-foreground-700 leading-relaxed">
                <p>
                  <strong className="text-foreground-900">{siteConfig.name}</strong>成立于2020年，是国内领先的水果植物类视频内容平台。
                  我们汇聚了香蕉视频、西瓜视频、蜜桃视频、樱花视频等丰富多样的水果植物相关视频资源，
                  致力于为广大用户提供<strong className="text-foreground-900">高清、免费、流畅</strong>的在线观看体验。
                </p>
                <p>
                  平台内容涵盖水果种植技术、植物科普知识、水果美食制作、花卉观赏指南等多个领域。
                  无论你是想要学习专业的种植技术，还是想要欣赏美丽的自然风光，
                  都能在{siteConfig.name}找到适合你的内容。
                </p>
                <p>
                  经过七年的发展，{siteConfig.name}已经积累了超过<strong className="text-primary-600">10,000部</strong>精品视频，
                  覆盖<strong className="text-primary-600">15个</strong>主要分类，日均观看量超过<strong className="text-primary-600">50万次</strong>，
                  服务着超过<strong className="text-primary-600">200万</strong>忠实用户。
                </p>
              </div>
            </div>
          </section>

          {/* Core Values */}
          <section className="py-14 md:py-20 bg-background-100" aria-label="核心价值观">
            <div className="max-w-[1100px] mx-auto px-4 md:px-6">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 text-center font-heading mb-4">
                核心价值观
              </h2>
              <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto mb-10"></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {values.map((v) => (
                  <div
                    key={v.title}
                    className="text-center p-6 rounded-xl bg-background-50 border border-background-200/60"
                  >
                    <div className="w-14 h-14 mx-auto rounded-full bg-accent-100 flex items-center justify-center mb-4">
                      <i className={`${v.icon} text-2xl text-accent-600`}></i>
                    </div>
                    <h3 className="text-base font-semibold text-foreground-900 mb-2 font-heading">{v.title}</h3>
                    <p className="text-sm text-foreground-500 leading-relaxed">{v.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Milestones */}
          <section className="py-14 md:py-20" aria-label="发展历程">
            <div className="max-w-[800px] mx-auto px-4 md:px-6">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 text-center font-heading mb-4">
                发展历程
              </h2>
              <div className="w-16 h-1 bg-primary-500 rounded-full mx-auto mb-10"></div>
              <div className="relative">
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-200 -translate-x-1/2"></div>
                <div className="space-y-8">
                  {milestones.map((m, i) => (
                    <div
                      key={m.year}
                      className={`relative flex items-start gap-6 ${
                        i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                      }`}
                    >
                      <div className="hidden md:block flex-1"></div>
                      <div className="relative z-10 w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <div className="w-3 h-3 rounded-full bg-white"></div>
                      </div>
                      <div className="flex-1 bg-background-50 border border-background-200/60 rounded-lg p-5 md:max-w-[320px]">
                        <span className="text-sm font-bold text-primary-600 whitespace-nowrap">{m.year}</span>
                        <p className="text-sm text-foreground-600 mt-1 leading-relaxed">{m.event}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Contact */}
          <section className="py-14 md:py-20 bg-background-100" aria-label="联系我们">
            <div className="max-w-[1100px] mx-auto px-4 md:px-6">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 text-center font-heading mb-4">
                联系我们
              </h2>
              <div className="w-16 h-1 bg-secondary-500 rounded-full mx-auto mb-10"></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {contactInfo.map((item) => (
                  <div
                    key={item.label}
                    className="text-center p-6 rounded-xl bg-background-50 border border-background-200/60 hover:border-secondary-200 transition-all duration-300"
                  >
                    <div className="w-12 h-12 mx-auto rounded-full bg-secondary-100 flex items-center justify-center mb-3">
                      <i className={`${item.icon} text-xl text-secondary-600`}></i>
                    </div>
                    <h3 className="text-sm font-medium text-foreground-800 mb-1">{item.label}</h3>
                    <p className="text-sm text-foreground-500">{item.value}</p>
                  </div>
                ))}
              </div>

              <div className="mt-14 text-center">
                <p className="text-sm text-foreground-500 mb-4">
                  有任何问题或建议？欢迎随时与我们取得联系
                </p>
                <Link
                  to="/"
                  className="whitespace-nowrap inline-flex items-center gap-2 px-8 py-3 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors no-underline cursor-pointer"
                >
                  <i className="ri-home-line"></i>
                  返回首页浏览视频
                </Link>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}